import {
  countLabel,
  indexCoveragePlan,
  indexMonitor,
  indexWatch,
} from '@seo/core'
import { defineCommand } from 'citty'
import {
  booleanArg,
  jsonFlag,
  listArg,
  numberArg,
  stringArg,
} from '../../args.js'
import { resolveSite } from '../../selection.js'
import { printJson, printKeyValue, printTable } from '../../utils.js'

export const indexWatchCommand = defineCommand({
  meta: {
    name: 'index-watch',
    description: 'Inspect URLs with GSC and alert on index status changes',
  },
  args: {
    site: {
      type: 'string',
      description: 'GSC property URL, for example sc-domain:example.com.',
    },
    urls: {
      type: 'string',
      description: 'Comma-separated URLs to inspect.',
    },
    sitemaps: {
      type: 'string',
      description:
        'Comma-separated XML sitemap URLs to plan quota-aware monitoring.',
    },
    plan: {
      type: 'boolean',
      default: false,
      description:
        'Plan sitemap URL allocation across GSC properties without inspecting URLs.',
    },
    properties: {
      type: 'string',
      description:
        'Comma-separated GSC properties to use for planning. Defaults to account properties.',
    },
    'daily-limit': {
      type: 'string',
      description: 'URL Inspection daily limit per property. Defaults to 2000.',
    },
    'target-days': {
      type: 'string',
      description:
        'Target number of days to inspect all monitored URLs. Defaults to 1.',
    },
    'max-urls': {
      type: 'string',
      description:
        'Maximum sitemap URLs to load for planning. Defaults to 50000.',
    },
    'inspect-limit': {
      type: 'string',
      description:
        'Maximum URLs to inspect in this run from sitemap mode. Defaults to 100.',
    },
    language: {
      type: 'string',
      description: 'Optional URL Inspection language code.',
    },
    json: {
      type: 'boolean',
      default: false,
      description: 'Print machine-readable JSON.',
    },
  },
  run: async ({ args }) => {
    const json = jsonFlag(args)
    const urls = listArg(args.urls)
    const site = await resolveSite({
      site: stringArg(args.site),
      options: { json },
    })

    const sitemaps = listArg(args.sitemaps)

    if (booleanArg(args.plan)) {
      if (!sitemaps.length) {
        throw new Error('Pass at least one sitemap with --sitemaps.')
      }
      const report = await indexCoveragePlan({
        site,
        sitemaps,
        properties: listArg(args.properties).length
          ? listArg(args.properties)
          : undefined,
        dailyLimit: numberArg(args['daily-limit']),
        targetCycleDays: numberArg(args['target-days']),
        maxUrls: numberArg(args['max-urls']),
      })
      if (json) {
        printJson(report)
        return
      }
      printKeyValue([
        ['Project site', report.site],
        ['Sitemap URLs', String(report.summary.urlCount)],
        ['Properties used', String(report.summary.properties)],
        ['Daily capacity', String(report.summary.dailyCapacity)],
        [
          'Estimated cycle',
          countLabel(report.summary.estimatedCycleDays, 'day'),
        ],
        ['Target cycle', countLabel(report.summary.targetCycleDays, 'day')],
        ['Suggested properties', String(report.summary.suggestedProperties)],
      ])
      if (report.properties.length) {
        printTable(
          ['Property', 'URLs', 'Cycle', 'Sample URL'],
          report.properties.map((property) => [
            property.property,
            property.urlCount,
            countLabel(property.cycleDays, 'day'),
            property.sampleUrls[0] ?? '-',
          ]),
        )
      }
      if (report.suggestions.length) {
        process.stdout.write('\nSuggested URL-prefix properties\n')
        printTable(
          ['Property', 'URLs', 'Current property', 'Cycle', 'Reason'],
          report.suggestions.map((suggestion) => [
            suggestion.property,
            suggestion.urlCount,
            suggestion.currentProperty,
            countLabel(suggestion.estimatedCycleDays, 'day'),
            suggestion.reason,
          ]),
        )
      }
      if (report.warnings.length) {
        process.stdout.write('\nWarnings\n')
        for (const warning of report.warnings.slice(0, 10)) {
          process.stdout.write(`- ${warning}\n`)
        }
      }
      return
    }

    if (sitemaps.length) {
      const report = await indexMonitor({
        site,
        sitemaps,
        properties: listArg(args.properties).length
          ? listArg(args.properties)
          : undefined,
        dailyLimit: numberArg(args['daily-limit']),
        inspectLimit: numberArg(args['inspect-limit']),
        maxUrls: numberArg(args['max-urls']),
        languageCode: stringArg(args.language),
      })
      if (json) {
        printJson(report)
        return
      }
      printKeyValue([
        ['Project site', report.site],
        ['Discovered sitemap URLs', String(report.source.discoveredUrls)],
        ['Invalid sitemap URLs', String(report.source.invalidUrls)],
        ['Inventory URLs', String(report.summary.inventoryUrls)],
        ['Properties used', String(report.summary.properties)],
        ['Daily capacity', String(report.summary.dailyCapacity)],
        ['Never attempted', String(report.summary.neverAttempted)],
        ['Never succeeded', String(report.summary.neverSucceeded)],
        ['Retry waiting', String(report.summary.retryWaiting)],
        ['Fresh', String(report.summary.fresh)],
        ['Stale', String(report.summary.stale)],
        ['Due', String(report.summary.due)],
        ['Selected this run', String(report.summary.selected)],
        ['Due but not selected', String(report.summary.unselectedDue)],
        ['Attempted', String(report.summary.attempted)],
        ['Inspected', String(report.summary.inspected)],
        ['Failed', String(report.summary.failed)],
        ['Quota blocked', String(report.summary.quotaBlocked)],
        ['Deferred', String(report.summary.deferred)],
        ['Current reviews', String(report.summary.currentIssues)],
        ['Regressions', String(report.summary.regressions)],
        ['Recoveries', String(report.summary.recoveries)],
        ['Changed', String(report.summary.changed)],
        ['New alerts', String(report.summary.alerts)],
        ['Skipped', String(report.summary.skipped)],
      ])
      if (report.properties.length) {
        printTable(
          [
            'Property',
            'Inventory',
            'Selected',
            'Inspected',
            'Failed',
            'Current',
            'Alerts',
          ],
          report.properties.map((property) => [
            property.property,
            property.inventoryUrls,
            property.selectedUrls,
            property.inspected,
            property.failed + property.quotaBlocked,
            property.currentIssues,
            property.alerts,
          ]),
        )
      }
      const issueItems = report.items
        .filter(
          (item) =>
            item.currentIssue ||
            item.changed ||
            item.inspectionStatus !== 'succeeded',
        )
        .slice(0, 50)
      if (issueItems.length) {
        process.stdout.write('\nIndex reviews, changes, and failed checks\n')
        printTable(
          ['Check', 'Change', 'Index state', 'Evidence', 'URL'],
          issueItems.map((item) => [
            item.inspectionStatus,
            item.changeKind,
            item.indexStatus,
            (item.errorCode ?? item.issueCodes.join(', ')) || 'none',
            item.url,
          ]),
        )
      }
      if (report.warnings.length) {
        process.stdout.write('\nWarnings\n')
        for (const warning of report.warnings.slice(0, 10)) {
          process.stdout.write(`- ${warning}\n`)
        }
      }
      return
    }

    if (!urls.length) throw new Error('Pass at least one URL with --urls.')

    const report = await indexWatch({
      site,
      urls,
      languageCode: stringArg(args.language),
      dailyLimit: numberArg(args['daily-limit']),
    })
    if (json) {
      printJson(report)
      return
    }
    printKeyValue([
      ['Project site', report.site],
      ['Inspection property', report.source.property],
      ['Attempted', String(report.summary.attempted)],
      ['Inspected', String(report.summary.inspected)],
      ['Failed', String(report.summary.failed)],
      ['Quota blocked', String(report.summary.quotaBlocked)],
      ['Deferred', String(report.summary.deferred)],
      ['Current reviews', String(report.summary.currentIssues)],
      ['Regressions', String(report.summary.regressions)],
      ['Recoveries', String(report.summary.recoveries)],
      ['Changed', String(report.summary.changed)],
      ['New alerts', String(report.summary.alerts)],
    ])
    printTable(
      ['Check', 'Change', 'Index state', 'Evidence', 'URL'],
      report.items.map((item) => [
        item.inspectionStatus,
        item.changeKind,
        item.indexStatus,
        (item.errorCode ?? item.issueCodes.join(', ')) || 'none',
        item.url,
      ]),
    )
  },
})
